/**
 * How an icon is named (ADR-111 decisions 1 and 2). A definition names its picture by a key of ours, the icon table
 * maps the key to a file of a purchased pack, and the icons' pointer says where the key's art stands in the atlas.
 * All three hold a key to the one form here.
 */

/**
 * An icon's key: 1 to 48 characters of `a-z`, `0-9` and `-`, starting with a letter. It names the picture, not the
 * thing that uses it (`flask-red-tilted`, not `minor-healing-potion`), so two things may share one.
 */
export const ICON_KEY_PATTERN = /^[a-z][a-z0-9-]{0,47}$/;

/**
 * The kinds of colour art: an `item` is drawn on a transparent background and is a thing a character carries, a
 * `tile` fills its square with a baked one, an `emoji` is transparent art an emote of the emote table names and no
 * rules record does (ADR-114 decision 7.1), and a `badge` is transparent art a staff badge names and no rules record
 * does (ADR-112 decision 6.2), and a `dialogue` is transparent art a dialogue choice ends with (an attribute check, a
 * background, a locked choice, a weight or Leave; ADR-136) and no rules record names. A kind is only ever added at the end.
 */
export const ICON_KINDS = ["item", "tile", "emoji", "badge", "dialogue"] as const;

/**
 * The kinds as a sentence lists them, for a message that says what a kind must be.
 */
export const ICON_KINDS_LISTED = `${ICON_KINDS.slice(0, -1).join(", ")} or ${ICON_KINDS[ICON_KINDS.length - 1] as string}`;

/**
 * One of `ICON_KINDS`.
 */
export type IconKind = (typeof ICON_KINDS)[number];

/**
 * Whether a value is an icon's key.
 *
 * @param value Any value.
 * @returns True for a string of the key's form.
 * @example
 * isIconKey("flask-red-tilted");
 * // true
 */
export function isIconKey(value: unknown): value is string {
  return typeof value === "string" && ICON_KEY_PATTERN.test(value);
}

/**
 * Whether a value is a kind of colour art.
 *
 * @param value Any value.
 * @returns True for one of `ICON_KINDS`.
 */
export function isIconKind(value: unknown): value is IconKind {
  return typeof value === "string" && (ICON_KINDS as readonly string[]).includes(value);
}
